'use client';

import NotFound from '@@components/pages/NotFound';
import Product from '@@components/pages/products/Product';
import ShowMoreButton from '@@components/pages/shared/ShowMoreButton';
import NLink from '@@components/utils/NLink';
import { ProductCategoryMap, ProductCategoryType, ProductsPageDefaultCategory } from '@@config/products';
import { ProductsQuery, useProductsQuery } from '@@graphql/client';
import useListLimit, { defaultMax } from '@@hooks/utils/useListLimit';
import { clx_ } from '@@libs/clx';
import { NPageProps } from '@@types/next';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { FiSearch } from 'react-icons/fi';
import { MdClear } from 'react-icons/md';
import { SyntheticEvent, useMemo, useState } from 'react';
import { NextPage } from 'next';
import css from '@@styles/pages/products.module.scss';

/**
 * Types
 */
export type ProductsPageProps = NPageProps<{
  category: ProductCategoryType;
}>;

/**
 * Initials
 */
const clx = clx_(css);
const select = (data: ProductsQuery) => data.products;
const variables = { available: true };
const options = { select };
const ProductDataSelector = (array: ReturnType<typeof select>) => array.groupBy((item) => item.category);

/**
 * Page: Products
 */
const ProductsPage: NextPage<ProductsPageProps> = ({ params: { category = ProductsPageDefaultCategory } }) => {
  const [Filter, setFilter] = useState('');
  const [TabValue, setTabValue] = useState<ProductCategoryType>(category);
  const { data = [], isLoading, error } = useProductsQuery(variables, options);
  const items = useMemo(() => {
    const itemGroups = ProductDataSelector(data);
    return (itemGroups[TabValue] ?? data).filter(({ name }) => name.toLowerCase().includes(Filter.toLowerCase()));
    // .sort(sortFnList.find(({ value }) => value === Sort)?.fn),
  }, [Filter, TabValue, data]);
  const [list, { limit, isLimited }, { increase }] = useListLimit(items, [TabValue]);
  const handleChange = (e: SyntheticEvent, v: ProductCategoryType) => setTabValue(v);

  if (!data.length && error) return <main className={clx('main-content')}>Error : {error.message}</main>;

  return (
    <main className={clx('main-content')}>
      {/* sm:w-fit sm:min-w-[25rem] */}

      <section className={clx('frame frame-card container mx-auto space-y-3')}>
        {/* Tabs */}
        <Tabs value={TabValue} onChange={handleChange} variant="fullWidth">
          {ProductCategoryMap.map(({ icon: Icon, label, colorSet, ...p }) => (
            // @ts-expect-error
            <Tab
              key={p.value} //
              href={'/products/' + p.value}
              label={<span className="hidden tablet:block">{label}</span>}
              icon={
                <Icon
                  size={22}
                  className={clx({
                    'text-orange-400': colorSet === 'orange', //
                    'text-blue-400': colorSet === 'blue',
                    'text-purple-400': colorSet === 'purple',
                  })}
                />
              }
              iconPosition="start"
              LinkComponent={NLink}
              prefetch
              replace
              shallow
              {...p}
            />
          ))}
        </Tabs>

        {/* Filters */}
        <TextField
          size="small"
          variant="outlined"
          value={Filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Termék neve"
          className="mt-3"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {Filter ? (
                  <IconButton edge="end" onClick={() => Filter && setFilter('')}>
                    <MdClear className="text-red-700" />
                  </IconButton>
                ) : (
                  <FiSearch className="text-white/50" />
                )}
              </InputAdornment>
            ),
          }}
          fullWidth
        />

        {/* Termekek */}
        <ul className="grid gap-3 sm:grid-cols-[repeat(auto-fill,minmax(30rem,1fr))]">
          {/* Loading */}
          {isLoading && Array.from({ length: defaultMax }, (_, i) => <Product variant="loading" key={i} />)}

          {/* Items */}
          {list.map((item, i) => (
            <Product key={item?.id ?? i} data={item} />
          ))}

          {/* Not found */}
          {!isLoading && !items.length && <NotFound />}

          {/* Show more */}
          <ShowMoreButton max={items.length} current={limit} increase={increase} disabled={isLimited} />
        </ul>
      </section>
    </main>
  );
};

export default ProductsPage;
